import md5 from 'md5';

// Based on implementation from:
//  https://github.com/tink-ab/tink-backend/blob/0847b0d0e98771ca3ac4fa3ac95002357c5d40f9/src/libraries/ab_test_group_calculation/src/main/java/se/tink/libraries/ab_test_group_calculation/ABTestingGroupCalculator.java

/**
 * Based on testGroupLimit, this will return true/false.
 * Example:
 * If testGroupLimit = 0.05, this fn is expected to return true for 5 % of calls
 * given that one of the hashArguments is an independent uniformly distributed
 * random variables, for instance userId.
 *
 * @param testGroupLimit Percentage of true cases for the test.
 * @param hashArguments The arguments that makes the test unique and independent
 * of concurrent tests. It can contain multiple arguments if multiple buckets
 * are desired. For instance [uuid, market, 'name-of-test'] will create buckets
 * per market, for the given test.
 *
 * @returns boolean if the hashArguments puts the end user in the test group.
 */

export type TestGroupGenerator = (testGroupLimit: number, hashArguments: string[]) => boolean;

export const isInTestGroup: TestGroupGenerator = (testGroupLimit, hashArguments) => {
  if (testGroupLimit < 0) {
    throw Error('Test group size must be larger than 0.');
  }

  if (testGroupLimit > 1) {
    throw Error('Test group size must be smaller than 1.');
  }

  if (hashArguments.length < 1) {
    throw Error('You need to have at least 1 hash argument.');
  }

  const testId = hashArguments.join('-');

  const digest = md5(testId);
  const digestPrelude = digest.substring(0, 6);

  // eslint-disable-next-line prefer-numeric-literals
  const value = parseInt(digestPrelude, 16) / parseInt('0xffffff', 16);

  return value < testGroupLimit;
};
